import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { CarnePagamento } from '@/components/carne-pagamento'
import { useParcelasParticipante, useMarcarParcelaComoPaga } from '@/hooks/use-eventos'
import { useParticipante } from '@/hooks/use-participantes'
import { useAlert } from '@/hooks/use-alert'

export function EventoCarnePage() {
  const { eventoSlug, participanteId } = useParams<{ eventoSlug: string; participanteId: string }>()
  const navigate = useNavigate()
  const { alert } = useAlert()

  const [parcelaSelecionada, setParcelaSelecionada] = useState<string | null>(null)
  const [valorPago, setValorPago] = useState('')
  const [metodoPagamento, setMetodoPagamento] = useState<'pix' | 'dinheiro' | 'cartao'>('pix')
  const [dataPagamento, setDataPagamento] = useState(new Date().toISOString().split('T')[0])
  const [observacoes, setObservacoes] = useState('')
  const [processando, setProcessando] = useState(false)

  // Buscar dados
  const { data: participante, isLoading: isLoadingParticipante } = useParticipante(participanteId)
  const { data: parcelas, isLoading: isLoadingParcelas } = useParcelasParticipante(participanteId)
  const marcarComoPaga = useMarcarParcelaComoPaga()

  const handleVoltar = () => {
    navigate(`/eventos/${eventoSlug}`)
  }

  const handleMarcarComoPaga = async (parcelaId: string) => {
    const parcela = parcelas?.find(p => p.id === parcelaId)
    if (parcela) {
      setValorPago(parcela.valor.toString())
    }
    setParcelaSelecionada(parcelaId)
  }

  const handleConfirmarPagamento = async () => {
    if (!parcelaSelecionada || !participanteId) return

    setProcessando(true)
    try {
      // Converter valor pago para número
      const valorNumerico = valorPago ? parseFloat(valorPago) : undefined

      await marcarComoPaga.mutateAsync({
        parcelaId: parcelaSelecionada,
        participanteId: participanteId,
        metodo: metodoPagamento,
        dataPagamento: dataPagamento,
        observacoes: observacoes || undefined,
        valor: valorNumerico,
      })

      alert.success('Sucesso!', 'Parcela marcada como paga.')
      setParcelaSelecionada(null)
      setValorPago('')
      setMetodoPagamento('pix')
      setDataPagamento(new Date().toISOString().split('T')[0])
      setObservacoes('')
    } catch (error) {
      console.error('Erro ao marcar parcela como paga:', error)
      alert.error('Erro', 'Não foi possível registrar o pagamento.')
    } finally {
      setProcessando(false)
    }
  }

  const handleCancelar = () => {
    setParcelaSelecionada(null)
    setValorPago('')
    setMetodoPagamento('pix')
    setDataPagamento(new Date().toISOString().split('T')[0])
    setObservacoes('')
  }

  if (isLoadingParticipante || isLoadingParcelas) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Carregando carnê...</p>
        </div>
      </div>
    )
  }

  if (!participante) {
    return (
      <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleVoltar}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Participante não encontrado</h1>
            <p className="text-muted-foreground">Não foi possível carregar os dados.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleVoltar}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">Carnê de Pagamento</h1>
          <p className="text-muted-foreground">
            Gerenciar parcelas de <span className="font-medium">{participante.nome}</span>
          </p>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {parcelas && parcelas.length > 0 ? (
            <CarnePagamento
              parcelas={parcelas}
              participanteNome={participante.nome}
              valorEntrada={
                Array.isArray((participante as any).pagamento)
                  ? (participante as any).pagamento[0]?.valor_entrada || null
                  : (participante as any).pagamento?.valor_entrada || null
              }
              onMarcarComoPaga={handleMarcarComoPaga}
              readonly={false}
            />
          ) : (
            <Card>
              <CardContent className="flex min-h-[400px] items-center justify-center">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Nenhuma parcela encontrada para este participante.
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    As parcelas são geradas automaticamente quando o participante é adicionado a um evento com
                    parcelamento habilitado.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Formulário de confirmação */}
        <div className="space-y-6">
          {parcelaSelecionada ? (
            <Card>
              <CardHeader>
                <CardTitle>Confirmar Pagamento</CardTitle>
                <CardDescription>Registre os detalhes do pagamento recebido</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="valorPago">Valor Pago</Label>
                  <Input
                    id="valorPago"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={valorPago}
                    onChange={(e) => setValorPago(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Valor sugerido da parcela. Você pode editar se necessário.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="metodo">Método de Pagamento</Label>
                  <Select
                    value={metodoPagamento}
                    onValueChange={(value) => setMetodoPagamento(value as 'pix' | 'dinheiro' | 'cartao')}
                  >
                    <SelectTrigger id="metodo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pix">Pix</SelectItem>
                      <SelectItem value="dinheiro">Dinheiro</SelectItem>
                      <SelectItem value="cartao">Cartão</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dataPagamento">Data do Pagamento</Label>
                  <Input
                    id="dataPagamento"
                    type="date"
                    value={dataPagamento}
                    onChange={(e) => setDataPagamento(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="observacoes">Observações (opcional)</Label>
                  <Textarea
                    id="observacoes"
                    placeholder="Ex: Pagamento confirmado via transferência..."
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                    rows={4}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Button onClick={handleConfirmarPagamento} disabled={processando}>
                    {processando ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      'Confirmar Pagamento'
                    )}
                  </Button>
                  <Button variant="outline" onClick={handleCancelar} disabled={processando}>
                    Cancelar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Instruções</CardTitle>
                <CardDescription>Como marcar parcelas como pagas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>1. Clique em "Marcar como Pago" na parcela desejada</p>
                <p>2. Selecione o método de pagamento utilizado</p>
                <p>3. Informe a data do pagamento</p>
                <p>4. Adicione observações se necessário</p>
                <p>5. Confirme o pagamento</p>
                <div className="mt-4 rounded-lg border bg-muted/50 p-3 text-xs">
                  <p className="font-medium">Dica:</p>
                  <p className="mt-1">
                    Você pode marcar parcelas individuais conforme forem sendo pagas. O sistema
                    acompanha automaticamente o progresso.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Informações do participante */}
          <Card>
            <CardHeader>
              <CardTitle>Dados do Participante</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">Nome:</span>
                <p className="font-medium">{participante.nome}</p>
              </div>
              {participante.email && (
                <div>
                  <span className="text-muted-foreground">Email:</span>
                  <p className="font-medium">{participante.email}</p>
                </div>
              )}
              {participante.telefone && (
                <div>
                  <span className="text-muted-foreground">Telefone:</span>
                  <p className="font-medium">{participante.telefone}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
